/**
=========================================================
* Soft UI Dashboard PRO React - v3.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiAvatar from "components/SuiAvatar";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

// Soft UI Dashboard PRO React example components
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import toast from "react-hot-toast";

import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import DoneIcon from "@mui/icons-material/Done";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import ChatIcon from "@mui/icons-material/Chat";
import NotificationAddIcon from "@mui/icons-material/NotificationAdd";

// @mui icons
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import DocumentIcon from "examples/Icons/Document";
import SettingsIcon from "examples/Icons/Settings";

// Images
import Rating from "@mui/material/Rating";
import { useNavigate } from "react-router-dom";

// Images
import ImgCrop from "antd-img-crop";
import { Upload } from "antd";
import { getBase64, dataURLtoFile, beforeUpload } from "utils/imageHelper";

import api from "services/api";
import { nanoid } from "nanoid";
import { useForm, Controller } from "react-hook-form";
import { addProtocol, formatAddress } from "utils/helper";
import FollowButton from "pages/social/followbutton";

const Header = ({
  store,
  hideButton = false,
  statusBar = null,
  editable = true,
  isSocial = false,
  updateStore,
}) => {
  const [editVisible, setEditVisible] = useState(false);
  const [avatarImageUrl, setAvatarImageUrl] = useState("");
  const [bgImageUrl, setBgImageUrl] = useState("");
  const [followUser, setFollowUser] = useState(null);
  const [chatRoomId, setChatRoomId] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    setValue,
    getValues,
    reset,

    control,
  } = useForm({});

  let instagram_link = "#";
  if (store.metadata && store.metadata.instagram_link) {
    instagram_link = store.metadata.instagram_link;
  }

  let facebook_link = "#";
  if (store.metadata && store.metadata.facebook_link) {
    facebook_link = store.metadata.facebook_link;
  }

  let twitter_link = "#";
  if (store.metadata && store.metadata.twitter_link) {
    twitter_link = store.metadata.twitter_link;
  }
  const navigate = useNavigate();

  useEffect(() => {
    reset({
      name: store.name,
      detail: store.detail,
    });
    setAvatarImageUrl(store?.metadata?.avatar);
    setBgImageUrl(store?.metadata?.background_image);
    if (isSocial && store.id) {
      api.social.list("ma", "all", store.id).then(({ data }) => {
        setFollowUser(data.users[0]);
        return data;
      });
      api.chatroom.addChatRoom({ userids: [store.id] }).then(({ data }) => {
        setChatRoomId(data.id);
        return data;
      });
    }
  }, [store]);

  const handleShowEdit = (e) => {
    setEditVisible(true);
  };

  const handleSave = async (data) => {
    let avatarUrl = avatarImageUrl;
    let bgUrl = bgImageUrl;

    if (avatarImageUrl?.startsWith("data")) {
      const file = dataURLtoFile(avatarImageUrl, "company_avatar");

      const uploadedImg = await api.uploads.create([file]).then(({ data }) => {
        const uploaded = data.uploads.map(({ url }) => url);
        return uploaded;
      });
      avatarUrl = uploadedImg && uploadedImg.length > 0 ? uploadedImg[0] : "";
    }

    if (bgImageUrl?.startsWith("data")) {
      const file = dataURLtoFile(bgImageUrl, nanoid());

      const uploadedImg = await api.uploads.create([file]).then(({ data }) => {
        const uploaded = data.uploads.map(({ url }) => url);
        return uploaded;
      });
      bgUrl = uploadedImg && uploadedImg.length > 0 ? uploadedImg[0] : "";
    }

    try {
      const payload = {
        name: data["name"],
        detail: data["detail"],
        metadata: {
          avatar: avatarUrl,
          background_image: bgUrl,
        },
      };
      await updateStore(payload);
      toast.success("Update Company information successfully!");
      setEditVisible(false);
    } catch (err) {
      toast.error("Update Company information failed!");
    }
  };

  const handleUploadImages = ({ file }) => {
    return getBase64(file);
    // return api.uploads.create([file])
  };

  const handleCropImgOk = async (data) => {
    try {
      const url = await getBase64(data);
      setAvatarImageUrl(url);
    } catch (err) {
      console.log(err);
    }
  };

  const handleCropBackgroundImgOk = async (data) => {
    try {
      const url = await getBase64(data);
      setBgImageUrl(url);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <SuiBox position="relative">
      <div>
        <SuiBox
          display="flex"
          alignItems="center"
          position="relative"
          minHeight={"18.75rem"}
          borderRadius="xl"
          sx={{
            backgroundImage: bgImageUrl
              ? `url(${bgImageUrl})`
              : ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                  `${linearGradient(
                    rgba(gradients.secondary.main, 0.2),
                    rgba(gradients.secondary.state, 0.4)
                  )}`,
            backgroundSize: "cover",
            backgroundPosition: "50%",
            overflow: "hidden",
          }}
        />
        {editVisible ? (
          <ImgCrop rotate shape="rect" aspect={3} onModalOk={handleCropBackgroundImgOk}>
            <Upload
              name="avatar"
              className="hover:cursor-pointer flex items-center"
              showUploadList={false}
              customRequest={handleUploadImages}
              beforeUpload={beforeUpload}
            >
              <div className="absolute top-0 left-0 w-full h-[18.75rem]" />
            </Upload>
          </ImgCrop>
        ) : null}
      </div>
      <Card
        className="overflow-visible"
        sx={{
          backdropFilter: `saturate(200%) blur(30px)`,
          backgroundColor: ({ functions: { rgba }, palette: { white } }) => rgba(white.main, 0.8),
          boxShadow: ({ boxShadows: { navbarBoxShadow } }) => navbarBoxShadow,
          position: "relative",
          mt: -8,
          mx: 3,
          py: 2,
          px: 2,
        }}
      >
        <form onSubmit={handleSubmit(handleSave)}>
          <div className="flex flex-wrap justify-start min-h-[50px] ml-8">
            <div className="w-24">
              <div className="absolute -top-10 left-8">
                {editVisible ? (
                  <ImgCrop rotate shape="round" onModalOk={handleCropImgOk}>
                    <Upload
                      name="avatar"
                      className="hover:cursor-pointer flex items-center"
                      showUploadList={false}
                      customRequest={handleUploadImages}
                      beforeUpload={beforeUpload}
                    >
                      <Tooltip title={"Click to change"} placement="top">
                        <SuiAvatar
                          src={avatarImageUrl}
                          alt="profile-image"
                          variant="circle"
                          size="xxl"
                          shadow="sm"
                        />
                      </Tooltip>
                    </Upload>
                  </ImgCrop>
                ) : (
                  <SuiAvatar
                    src={avatarImageUrl}
                    alt="profile-image"
                    variant="circle"
                    size="xxl"
                    shadow="sm"
                  />
                )}
              </div>
            </div>
            <div className="flex justify-between grow  xsmall:pt-2 xsmall:pb-2 small:pt-0">
              {statusBar ? (
                statusBar
              ) : (
                <div className="flex flex-wrap justify-start w-full ">
                  <div className="flex flex-wrap items-center">
                    <Tooltip
                      title={
                        <SuiTypography
                          sx={{ mr: 2, ml: 1 }}
                          variant="text"
                          fontWeight="regular"
                          color="secondary"
                        >
                          {store?.rating >= 0 ? (store.rating / 20).toFixed(1) : 0} of 5 stars
                        </SuiTypography>
                      }
                    >
                      <SuiBox>
                        <Rating
                          name="half-rating-read"
                          value={store?.rating >= 0 ? (store.rating / 20).toFixed(1) : 0}
                          precision={0.1}
                          readOnly
                        />
                      </SuiBox>
                    </Tooltip>

                    <div className="flex">
                      {instagram_link == "#" ? null : (
                        <a
                          target={"_blank"}
                          href={addProtocol(instagram_link)}
                          className="text-gray-800 mr-2"
                        >
                          <InstagramIcon />
                        </a>
                      )}
                      {facebook_link == "#" ? null : (
                        <a
                          target={"_blank"}
                          href={addProtocol(facebook_link)}
                          className="text-gray-800 mr-2"
                        >
                          <FacebookIcon />
                        </a>
                      )}
                      {twitter_link == "#" ? null : (
                        <a
                          target={"_blank"}
                          href={addProtocol(twitter_link)}
                          className="text-gray-800 mr-2"
                        >
                          <TwitterIcon />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {editable &&
                (editVisible ? (
                  <div className="flex items-center xsmall:flex-wrap small:flex-none">
                    <ImgCrop rotate shape="round" onModalOk={handleCropImgOk}>
                      <Upload
                        name="avatar"
                        className="hover:cursor-pointer"
                        showUploadList={false}
                        customRequest={handleUploadImages}
                        beforeUpload={beforeUpload}
                      >
                        <SuiButton
                          variant="text"
                          color="dark"
                          size="small"
                          className="min-w-0 px-1 py-0"
                        >
                          <span className="flex items-center justify-center">
                            <div className="mr-1">
                              <CloudUploadIcon />
                            </div>
                            <SuiTypography
                              variant="text"
                              textTransform="uppercase"
                              className="xsmall:hidden small:inline"
                            >
                              Avatar
                            </SuiTypography>
                          </span>
                        </SuiButton>
                      </Upload>
                    </ImgCrop>

                    <ImgCrop rotate shape="rect" aspect={3} onModalOk={handleCropBackgroundImgOk}>
                      <Upload
                        name="avatar"
                        className="hover:cursor-pointer flex items-center "
                        showUploadList={false}
                        customRequest={handleUploadImages}
                        beforeUpload={beforeUpload}
                      >
                        <SuiButton
                          variant="text"
                          color="dark"
                          size="small"
                          className="min-w-0 px-1 py-0"
                        >
                          <span className="flex items-center justify-center">
                            <div className="mr-1">
                              <WallpaperIcon />
                            </div>
                            <SuiTypography
                              variant="text"
                              textTransform="uppercase"
                              className="xsmall:hidden small:inline"
                            >
                              Background Image
                            </SuiTypography>
                          </span>
                        </SuiButton>
                      </Upload>
                    </ImgCrop>

                    <Tooltip title={"Cancel"} placement="top">
                      <IconButton
                        aria-label="clear"
                        size="small"
                        onClick={() => setEditVisible(false)}
                      >
                        <ClearIcon className="text-uss-red" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={"Save"} placement="top">
                      <IconButton aria-label="done" size="small" type="submit">
                        <DoneIcon color="success" />
                      </IconButton>
                    </Tooltip>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Tooltip title={"Edit"} placement="top">
                      <IconButton aria-label="edit" size="small" onClick={handleShowEdit}>
                        <EditIcon color="dark" />
                        <SuiTypography
                          variant="h6"
                          fontWeight="bold"
                          textTransform="uppercase"
                          sx={{ ml: 1, fontSize: 14 }}
                          className="xsmall:hidden small:inline"
                        >
                          EDIT
                        </SuiTypography>
                      </IconButton>
                    </Tooltip>
                  </div>
                ))}
            </div>
          </div>

          <div className="mt-2 ml-4">
            <Grid container>
              <Grid item md={8} sm={12} xs={12}>
                <SuiTypography variant="h5" fontWeight="bold" color="dark">
                  {editVisible ? (
                    <div className="my-1">
                      <SuiInput
                        placeholder="Business Name"
                        required
                        {...register(`name`, { maxLength: 50 })}
                        className="max-w-xs"
                      />
                      {errors.name?.type === "maxLength" ? (
                        <SuiTypography
                          variant="text"
                          fontWeight="regular"
                          fontSize="12px"
                          color="error"
                          className="ml-2"
                        >
                          Maxmium length should not exceed 50 characters
                        </SuiTypography>
                      ) : null}
                    </div>
                  ) : (
                    store?.name
                  )}
                </SuiTypography>
                <div>
                  <SuiTypography variant="h6" fontWeight="regular" color="dark">
                    {editVisible ? (
                      <div className="my-1">
                        <SuiInput
                          placeholder="Brief descripe your capabilities/services(max 100 characters)"
                          required
                          {...register(`detail`, { maxLength: 100 })}
                          className="max-w-lg"
                        />
                        {errors.detail?.type === "maxLength" ? (
                          <SuiTypography
                            variant="text"
                            fontWeight="regular"
                            fontSize="12px"
                            color="error"
                            className="ml-2"
                          >
                            Maxmium length should not exceed 100 characters
                          </SuiTypography>
                        ) : null}
                      </div>
                    ) : store.detail ? (
                      store.detail
                    ) : (
                      <SuiTypography
                        variant="h6"
                        color="text"
                        fontWeight="regular"
                        textTransform="capitalize"
                      >
                        Brief one sentence description of your capabilities/services
                      </SuiTypography>
                    )}
                  </SuiTypography>
                </div>
                <SuiTypography variant="button" fontWeight="regular" color="text">
                  {store.metadata && store.metadata.address
                    ? formatAddress(store.metadata.address)
                    : ""}
                </SuiTypography>
              </Grid>
              {isSocial ? (
                <Grid item md={4} sm={12} xs={12}>
                  <div className="grid grid-cols-2">
                    <div className="my-1 md:col-span-1 sm:col-span-2 xs:col-span-2">
                      <SuiButton
                        component={Link}
                        to={"/messages/general?chatid=" + chatRoomId}
                        size="small"
                        className="small:px-2 medium:px-4 whitespace-nowrap"
                      >
                        <span className="flex items-center justify-center w-max-24">
                          <div className="mr-1">
                            <ChatIcon sx={{ height: "20px", width: "20px" }} />
                          </div>
                          <SuiTypography
                            variant="text"
                            textTransform="uppercase"
                            className="xsmall:hidden small:inline"
                          >
                            Message
                          </SuiTypography>
                        </span>
                      </SuiButton>
                    </div>

                    {/* <div className="my-1 md:col-span-1 sm:col-span-2 xs:col-span-2">
                      {followUser && <FollowButton user={followUser} size="medium" />}
                    </div> */}
                  </div>
                </Grid>
              ) : null}
              {hideButton ? null : (
                <Grid item md={4} sm={12} xs={12}>
                  <Grid container>
                    <Grid item md={6} sm={6} xs={6} sx={{ my: 0.5 }}>
                      <SuiButton
                        size="small"
                        className="small:px-2 medium:px-4 whitespace-nowrap"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate("/edit-profile");
                        }}
                      >
                        <span className="flex items-center justify-center w-max-24">
                          <div className="mr-1">
                            <DocumentIcon />
                          </div>
                          <SuiTypography
                            variant="text"
                            textTransform="uppercase"
                            className="xsmall:hidden small:inline"
                          >
                            Edit Profile
                          </SuiTypography>
                        </span>
                      </SuiButton>
                    </Grid>
                    <Grid item md={6} sm={6} xs={6} sx={{ my: 0.5 }}>
                      <SuiButton
                        size="small"
                        className="small:px-2 medium:px-4 "
                        onClick={(e) => {
                          e.preventDefault();
                          navigate("/setting");
                        }}
                      >
                        <span className="flex items-center justify-center w-max-24">
                          <div className="mr-1">
                            <SettingsIcon />
                          </div>
                          <SuiTypography
                            variant="text"
                            textTransform="uppercase"
                            className="xsmall:hidden small:inline"
                          >
                            settings
                          </SuiTypography>
                        </span>
                      </SuiButton>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </div>
          {statusBar ? (
            <div className="flex justify-end">
              <div className="flex justify-between h-4">
                <div className="flex items-center ml-4 ">
                  <Rating
                    name="half-rating-read"
                    value={store?.rating >= 0 ? (store.rating / 20).toFixed(1) : 0}
                    precision={0.1}
                    readOnly
                  />
                  <SuiTypography
                    sx={{ mr: 2, ml: 1 }}
                    variant="text"
                    fontWeight="regular"
                    color="secondary"
                  >
                    {store?.rating >= 0 ? (store.rating / 20).toFixed(1) : 0}
                  </SuiTypography>
                </div>
              </div>
            </div>
          ) : null}
        </form>
      </Card>
    </SuiBox>
  );
};

export default Header;
